import { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";

const LOGO_URL = "/logo-shield.png";

const navLinks = [
  { label: "Practice Areas", href: "#practice" },
  { label: "About", href: "#difference" },
  { label: "Partners", href: "#partners" },
  { label: "Contact", href: "#contact" },
];

const NavLogo = ({ onNavClick }) => (
  <a href="#hero" onClick={() => onNavClick("#hero")} className="flex items-center gap-3.5" data-testid="nav-logo">
    <img src={LOGO_URL} alt="LexSant" className="h-14 md:h-16 w-auto" />
    <span className="flex flex-col leading-none">
      <span className="font-heading text-2xl md:text-[26px] text-white tracking-tight">LexSant</span>
      <span className="text-[9px] md:text-[10px] tracking-[0.28em] uppercase text-gold font-body mt-1.5 hidden sm:block">Legal &amp; Advisory LLP</span>
    </span>
  </a>
);

const DesktopNav = ({ onNavClick }) => (
  <div className="hidden md:flex items-center gap-12">
    {navLinks.map((link) => (
      <button
        key={link.label}
        onClick={() => onNavClick(link.href)}
        data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
        className="text-sm font-body tracking-wide text-gray-300 hover:text-gold transition-colors duration-300"
      >
        {link.label}
      </button>
    ))}
  </div>
);

const DesktopCTA = ({ onNavClick }) => (
  <div className="hidden md:flex items-center gap-4">
    <a
      href="tel:+919914601473"
      data-testid="nav-call-btn"
      className="text-sm text-gold hover:text-gold-hover transition-colors flex items-center gap-2 mr-1"
    >
      <Phone className="w-4 h-4" />
      <span>+91-9914601473</span>
    </a>
    <button
      onClick={() => onNavClick("#contact")}
      data-testid="nav-cta-btn"
      className="bg-gold text-navy-dark px-6 py-2.5 text-sm font-semibold tracking-wide hover:bg-gold-hover transition-colors duration-300"
    >
      Book a Consultation
    </button>
  </div>
);

const MobileMenu = ({ onNavClick }) => (
  <div className="md:hidden bg-navy-base/95 backdrop-blur-xl border-t border-gold/10" data-testid="mobile-menu">
    <div className="px-6 py-6 space-y-4">
      {navLinks.map((link) => (
        <button
          key={link.label}
          onClick={() => onNavClick(link.href)}
          data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
          className="block w-full text-left text-base text-gray-300 hover:text-gold py-2 transition-colors"
        >
          {link.label}
        </button>
      ))}
      <div className="pt-4 border-t border-gold/10">
        <button
          onClick={() => onNavClick("#contact")}
          data-testid="mobile-cta-btn"
          className="w-full bg-gold text-navy-dark py-3 text-sm font-semibold tracking-wide hover:bg-gold-hover transition-colors"
        >
          Book a Consultation
        </button>
      </div>
    </div>
  </div>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-navy-base/90 backdrop-blur-xl border-b border-gold/10 shadow-lg shadow-black/20"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20 md:h-24">
          <NavLogo onNavClick={handleNavClick} />
          <DesktopNav onNavClick={handleNavClick} />
          <DesktopCTA onNavClick={handleNavClick} />
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
            className="md:hidden text-white p-2"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && <MobileMenu onNavClick={handleNavClick} />}
    </nav>
  );
}
